import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { hexContrast, isHex, normalizeHex, randomHex } from '@/lib/color'
import { sanitizeHexInput } from '@/lib/registry'
import { lookup } from '@/lib/api'
import type { RegistryColor } from '@/lib/registry'

interface ColorPickerProps {
  initialHex?: string
  className?: string
  /**
   * Called whenever the swatch settles on a hex, and again when the registry
   * lookup for it answers: `match` is undefined while the lookup is in flight,
   * null when the color is available, or the registered color.
   */
  onChange?: (hex: string, match: RegistryColor | null | undefined) => void
  /** Hide the "Register Now" link (on the register page itself). */
  linkToRegister?: boolean
}

/**
 * The color picker: a big swatch you
 * can click to pick a color, a hex field, and a line that says whether the
 * color is already named or still available.
 */
export default function ColorPicker({ initialHex, className = '', onChange, linkToRegister = true }: ColorPickerProps) {
  const [hex, setHex] = useState(() => (initialHex && isHex(initialHex) ? normalizeHex(initialHex) : randomHex()))
  const [text, setText] = useState(hex)
  const [looked, setLooked] = useState<{ hex: string; match: RegistryColor | null } | null>(null)
  // undefined while the lookup for the current hex is in flight
  const match = looked && looked.hex === hex ? looked.match : undefined

  const contrast = hexContrast(hex)

  useEffect(() => {
    onChange?.(hex, match)
  }, [hex, match, onChange])

  // Look the color up (debounced) whenever the swatch settles on a new hex.
  useEffect(() => {
    let alive = true
    const t = setTimeout(() => {
      lookup(hex).then((c) => {
        if (alive) setLooked({ hex, match: c })
      })
    }, 250)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [hex])

  function apply(next: string) {
    const n = normalizeHex(next)
    setHex(n)
    setText(n)
  }

  function onText(raw: string) {
    const v = sanitizeHexInput(raw)
    setText(v)
    if (/^#[0-9a-f]{6}$/.test(v)) setHex(v)
  }

  const textColor = contrast === 'black' ? '#111111' : '#ffffff'

  return (
    <div className={`w-full max-w-[360px] font-display ${className}`}>
      {/* The whole swatch is a label for the (visually hidden) color input, which sits
          over the "click to pick" chip so the browser's picker popup opens beside it. */}
      <label
        className="relative block aspect-square w-full cursor-pointer overflow-hidden rounded-sm shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] transition-colors"
        style={{ backgroundColor: hex }}
      >
        <span className="sr-only">Pick a color</span>
        <span
          className="absolute bottom-3 right-3 rounded px-2 py-1 text-xs font-body tracking-wide"
          style={{ color: textColor, backgroundColor: contrast === 'black' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.35)' }}
        >
          click to pick
          <input
            type="color"
            value={hex}
            onChange={(e) => apply(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label="Color picker"
          />
        </span>
      </label>

      <div className="mt-3 flex items-center gap-3 px-3 py-2" style={{ backgroundColor: hex, color: textColor }}>
        <input
          type="text"
          value={text}
          onChange={(e) => onText(e.target.value)}
          maxLength={7}
          spellCheck={false}
          aria-label="Hex color code"
          className="w-[6.2em] shrink-0 bg-white/90 px-2 py-1 font-body text-lg font-normal tracking-wide text-neutral-900 outline-none focus:ring-2 focus:ring-black/30"
        />
        <div className="min-w-0 flex-1 text-lg leading-tight" aria-live="polite">
          {match === undefined ? (
            <span className="opacity-60">…</span>
          ) : match ? (
            <Link to={`/color/${match.slug}`} className="no-underline hover:underline" style={{ color: textColor }}>
              {match.name}
              <sup>CM</sup>
            </Link>
          ) : linkToRegister ? (
            <Link to={`/register?hex=${hex.slice(1)}`} className="no-underline hover:underline" style={{ color: textColor }}>
              Available - Register Now!
            </Link>
          ) : (
            <span>Available!</span>
          )}
        </div>
      </div>

      <div className="mt-1 text-right">
        <button
          type="button"
          onClick={() => apply(randomHex())}
          className="font-body text-[13px] italic text-[#007bff] hover:underline"
        >
          RANDOMIZE!
        </button>
      </div>
    </div>
  )
}
